<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '@/components/common/BaseButton.vue'
import NotificationBell from '@/components/common/NotificationBell.vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { X_PROFILE_URL } from '@/config/social'
import { useTheme } from '@/composables'
import { useAuthStore } from '@/stores/auth'
import { useRealtimeStore } from '@/stores/realtime'
import logoCutout from '@/assets/images/logo-infinity-cutout.png'
import {
  PhGear,
  PhMoon,
  PhPlus,
  PhSignOut,
  PhSun,
  PhUser,
  PhXLogo,
} from '@phosphor-icons/vue'

defineOptions({ name: 'AppDesktopNav' })

/** `account` has no top-level link: it marks the account menu (profile pages). */
export type NavSection = 'home' | 'browse' | 'inbox' | 'dashboard' | 'account'

defineProps<{ active?: NavSection }>()

const links = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'browse', label: 'Browse', path: '/browse/' },
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard/' },
  { id: 'inbox', label: 'Inbox', path: '/inbox/' },
] as const satisfies readonly { id: NavSection; label: string; path: string }[]

const router = useRouter()
const authStore = useAuthStore()
const realtime = useRealtimeStore()
const { isDark, setPreference } = useTheme()

const visibleLinks = computed(() =>
  authStore.isAuthenticated ? links : links.filter(link => link.id === 'home' || link.id === 'browse'),
)

function badgeFor(id: NavSection) {
  return id === 'inbox' ? realtime.unreadCount : 0
}

function badgeText(count: number) {
  return count > 9 ? '9+' : String(count)
}

function go(path: string) {
  if (router.currentRoute.value.path !== path) void router.push(path)
}

async function logout() {
  await authStore.logout()
  void router.push('/login/')
}
</script>

<template>
  <!-- Desktop only: phones keep PageHeader + AppTabbar. -->
  <header class="app-desktop-nav relative z-30 hidden flex-none border-b border-line bg-canvas md:block">
    <div class="mx-auto flex h-16 w-full max-w-7xl items-center gap-8 px-6 lg:px-8">
      <button
        type="button"
        class="flex shrink-0 cursor-pointer items-center gap-2.5"
        aria-label="TalentSwap home"
        @click="go('/')"
      >
        <img :src="logoCutout" alt="" class="h-8 w-8" />
        <span class="text-lg font-bold tracking-tight text-ink">TalentSwap</span>
      </button>

      <nav class="flex items-center gap-1" aria-label="Primary">
        <button
          v-for="link in visibleLinks"
          :key="link.id"
          type="button"
          class="relative flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
          :class="active === link.id ? 'bg-surface-2 text-accent-cyan' : 'text-ink-4 hover:bg-surface hover:text-ink'"
          :aria-current="active === link.id ? 'page' : undefined"
          @click="go(link.path)"
        >
          {{ link.label }}
          <span
            v-if="badgeFor(link.id)"
            class="flex h-4 min-w-4 items-center justify-center rounded-full bg-cta px-1 text-[10px] font-bold leading-4 text-cta-foreground"
          >
            {{ badgeText(badgeFor(link.id)) }}
            <span class="sr-only">&nbsp;unread</span>
          </span>
        </button>
      </nav>

      <div class="ml-auto flex items-center gap-2">
        <template v-if="authStore.isAuthenticated">
          <BaseButton variant="primary" size="sm" @click="go('/create-listing/')">
            <PhPlus class="mr-1 h-4 w-4" weight="bold" />
            Create Listing
          </BaseButton>

          <NotificationBell />
        </template>

        <BaseButton
          variant="ghost"
          size="sm"
          :href="X_PROFILE_URL"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Follow us on X"
        >
          <PhXLogo class="h-5 w-5" weight="bold" />
        </BaseButton>

        <BaseButton
          variant="ghost"
          size="sm"
          :aria-label="isDark ? 'Switch to light theme' : 'Switch to dark theme'"
          @click="setPreference(isDark ? 'light' : 'dark')"
        >
          <PhSun v-if="isDark" class="h-5 w-5" />
          <PhMoon v-else class="h-5 w-5" />
        </BaseButton>

        <DropdownMenu v-if="authStore.isAuthenticated">
          <DropdownMenuTrigger as-child>
            <button
              type="button"
              class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-linear-to-tr from-cyan-400 to-purple-500 p-[1.5px]"
              :class="{ 'ring-2 ring-accent-cyan ring-offset-2 ring-offset-canvas': active === 'account' }"
              aria-label="Account menu"
              :aria-current="active === 'account' ? 'page' : undefined"
            >
              <span class="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-canvas">
                <img
                  v-if="authStore.user?.avatarURL"
                  :src="authStore.user.avatarURL"
                  :alt="authStore.user.username"
                  class="h-full w-full object-cover"
                />
                <PhUser v-else class="h-4 w-4 text-ink-5" />
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-48">
            <DropdownMenuItem @click="go(`/profile/${authStore.user?.id}/`)">
              <PhUser class="mr-2 h-4 w-4" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem @click="go('/settings/')">
              <PhGear class="mr-2 h-4 w-4" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" @click="logout">
              <PhSignOut class="mr-2 h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <BaseButton v-else variant="primary" size="sm" @click="go('/login/')">Sign in</BaseButton>
      </div>
    </div>
  </header>
</template>
